import { useCallback, useState } from "react";
import api, { formatApiError } from "@/lib/api";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// status: idle | processing | success | pending | failed | cancelled
export function useCheckout(course) {
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [mockOrder, setMockOrder] = useState(null);

  const finish = (s, msg, data = null) => { setStatus(s); setMessage(msg); if (data) setResult(data); };

  const verify = useCallback(async (order_id, razorpay_payment_id, razorpay_signature) => {
    setStatus("processing"); setMessage("Verifying your payment securely…");
    try {
      const { data } = await api.post("/payments/verify", { order_id, razorpay_payment_id, razorpay_signature });
      if (data.status === "paid") finish("success", data.duplicate ? "This payment was already processed — you're enrolled." : "Payment successful! You are now enrolled.", data);
      else finish("pending", "Your payment is being confirmed by the bank. Access will be granted automatically once it is captured.", data);
    } catch (e) {
      finish("failed", formatApiError(e.response?.data?.detail) || "Payment verification failed.");
    }
  }, []);

  const openRazorpay = async (o) => {
    if (!(await loadRazorpayScript())) return finish("failed", "Could not load Razorpay checkout. Check your connection and try again.");
    const rzp = new window.Razorpay({
      key: o.key_id, amount: o.amount, currency: o.currency, order_id: o.order_id,
      name: "PilotYatra", description: o.course_name, prefill: o.prefill, theme: { color: "#f97316" },
      handler: (res) => verify(res.razorpay_order_id, res.razorpay_payment_id, res.razorpay_signature),
      modal: { ondismiss: () => setStatus((s) => (s === "processing" ? "cancelled" : s)) },
    });
    rzp.on("payment.failed", (r) => {
      const reason = r.error?.description || "Payment failed";
      api.post(`/payments/${o.order_id}/failed`, { reason, razorpay_payment_id: r.error?.metadata?.payment_id }).catch(() => {});
      finish("failed", reason);
    });
    rzp.open();
  };

  const pay = async () => {
    setStatus("processing"); setMessage("Creating your secure order…"); setResult(null);
    try {
      const { data } = await api.post("/payments/create-order", { course_id: course.id });
      if (data.free) return finish("success", "You're enrolled in this free course!", { enrollment: data.enrollment });
      setMessage("Complete the payment in the Razorpay window…");
      if (data.mock) setMockOrder(data);
      else await openRazorpay(data);
    } catch (e) {
      finish("failed", formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const payMock = async (succeed) => {
    const o = mockOrder;
    setMockOrder(null);
    if (!succeed) {
      await api.post(`/payments/${o.order_id}/failed`, { reason: "Cancelled in test checkout" }).catch(() => {});
      return finish("failed", "Payment was not completed.");
    }
    await verify(o.order_id, `pay_mock_${Date.now()}`, "mock_signature");
  };

  return { status, message, result, pay, mockOrder, payMock, reset: () => setStatus("idle") };
}
